import React from 'react';
import { MailIcon, MessageCircleIcon, PhoneIcon } from 'lucide-react';
import { priceLists } from '../../data/priceLists';
import { contact as contactInfo } from '../../data/contact';
import type { ContactTopic } from '../../data/contactTopics';
import type { Course, Destination } from '../../types/content';
interface ContactSummaryProps {
  topic?: ContactTopic;
  course?: Course;
  location?: Destination;
}

// Live summary of what the visitor has chosen, plus the direct ways to reach the team.
export function ContactSummary({
  topic,
  course,
  location
}: ContactSummaryProps) {
  const price = course?.group === 'english' && location?.priceList ? priceLists.find(l => l.slug === location.priceList)?.fromPrice : undefined;
  const rows = [{
    label: 'Topic',
    value: topic?.label
  }, {
    label: 'Programme',
    value: course?.name,
    hidden: topic && !topic.steps.includes('course')
  }, {
    label: 'Location',
    value: location ? `${location.name}, ${location.country}` : undefined,
    hidden: topic && !topic.steps.includes('location')
  }].filter(row => !row.hidden);
  return <aside aria-label="Your enquiry" className="space-y-6 lg:sticky lg:top-[104px]" data-id="element-138" data-mp="9d65z4wqwlsb">
      <div className="overflow-hidden rounded-[28px] bg-white" data-id="element-139" data-mp="4eml77tw1g1j">
        {location ? <img src={location.image} alt="" className="aspect-[16/9] w-full object-cover" data-id="element-140" data-mp="4gphg9lmw5ep" /> : null}
        <div className="p-6 md:p-7" data-id="element-141" data-mp="3vn0b3kobe3w0">
          <p className="type-label text-muted" data-id="element-142" data-mp="3rju9wrg272we">Your enquiry</p>
          <dl className="mt-4 divide-y divide-line" data-id="element-143" data-mp="3rlf4ui6acnus">
            {rows.map(row => <div key={row.label} className="flex items-baseline justify-between gap-4 py-3" data-id="element-144" data-mp="3mrawpvlaa9s4">
                <dt className="type-small text-muted" data-id="element-145" data-mp="3htsj3rwbn6ds">{row.label}</dt>
                <dd className={`text-right type-list ${row.value ? 'text-ink' : 'text-muted'}`} data-id="element-146" data-mp="3cvh9ni9xuxw3">{row.value ?? 'Not chosen yet'}</dd>
              </div>)}
          </dl>
          {course ? <dl className="mt-4 grid grid-cols-2 gap-3" data-id="element-147" data-mp="33usziyno1hoh">
              {course.facts.slice(0, 2).map(fact => <div key={fact.label} className="rounded-[16px] bg-cream p-3" data-id="element-148" data-mp="2t73289nrgn5u">
                  <dt className="type-small text-muted" data-id="element-149" data-mp="2y8sajhjlmmq8">{fact.label}</dt>
                  <dd className="mt-0.5 type-small font-bold text-ink" data-id="element-150" data-mp="2k8hlmk1uni7p">{fact.value}</dd>
                </div>)}
            </dl> : null}
          {price ? <p className="mt-4 type-small text-body" data-id="element-151" data-mp="3cwlrb5kf7vae">
              Tuition from <span className="type-card-title text-ink" data-id="element-152" data-mp="3cxf2ciqex6yc">{price}</span> per week
            </p> : null}
        </div>
      </div>

      <div className="rounded-[28px] bg-white p-6 md:p-7" data-id="element-153" data-mp="33728fzwpk1fj">
        <p className="type-card-title text-ink" data-id="element-154" data-mp="336k4eh3t0ix2">Prefer to talk now?</p>
        <p className="mt-1 type-small text-body" data-id="element-155" data-mp="384vf0u1xaxgc">Our admissions team speaks 20+ languages.</p>
        <ul className="mt-4 space-y-2" data-id="element-156" data-mp="3vugagcf9jn6i">
          <SummaryLink href={contactInfo.whatsappLink} external icon={<MessageCircleIcon className="h-4 w-4" aria-hidden="true" data-id="element-158" data-mp="4hvddvwryap3" />} label="WhatsApp" value={contactInfo.whatsappDisplay} data-id="element-157" data-mp="3vteld41ex05s" />
          <SummaryLink href={contactInfo.emailLink} icon={<MailIcon className="h-4 w-4" aria-hidden="true" data-id="element-160" data-mp="9uabinubh8vh" />} label="Email" value={contactInfo.email} data-id="element-159" data-mp="dtssx9mxj1tk" />
          <SummaryLink href={contactInfo.phoneLink} icon={<PhoneIcon className="h-4 w-4" aria-hidden="true" data-id="element-162" data-mp="nknf6e06e1f0" />} label="Call" value={contactInfo.phoneDisplay} data-id="element-161" data-mp="nlyhsahvx34j" />
        </ul>
      </div>
    </aside>;
}
function SummaryLink({
  href,
  icon,
  label,
  value,
  external
}: {
  href: string;
  icon: React.ReactNode;
  label: string;
  value: string;
  external?: boolean;
}) {
  return <li data-id="element-163" data-mp="3n2wt9gkwrajv">
      <a href={href} target={external ? '_blank' : undefined} rel={external ? 'noreferrer' : undefined} className="flex items-center gap-3 rounded-[16px] px-3 py-2.5 transition-colors duration-150 hover:bg-mist" data-id="element-164" data-mp="4uxlju85floo">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-cream text-brand-press" data-id="element-165" data-mp="1g8sziznbhoeb">{icon}</span>
        <span className="min-w-0" data-id="element-166" data-mp="1bd3xmpntrveu">
          <span className="block type-small text-muted" data-id="element-167" data-mp="3gztj6578jctb">{label}</span>
          <span className="block truncate type-small font-bold text-ink" data-id="element-168" data-mp="3h1mtpvkyyh6l">{value}</span>
        </span>
      </a>
    </li>;
}